import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Field, Select, Option, Alert, StatusType } from '@platform/witems';
import useDebounce from '../../../hooks/useDebounce';
import { usePatientRecord } from '../../../state/data/patient';
import {
  useCohortProducts, useAddressSuggestions, useCreateDeviceOrder, useVerifyAddress,
} from '../../../state/data/dropshipment';
import { Constants } from '../../../config/constants';
import { productImageUrl, orderRefsFor } from '../../../logic/devices';
import { COUNTRIES, statesForCountry, toStateName, parseAddressSuggestion } from '../../../logic/geo';
import DeviceGlyph from './DeviceGlyph';
import useDemoMode from '../../../hooks/useDemoMode';

function CatalogImage({ item, name, height = 110 }) {
  const [failed, setFailed] = useState(false);
  const url = productImageUrl(item);
  if (url && !failed) {
    return <img src={url} alt={name} onError={() => setFailed(true)} style={{ width: '100%', height, objectFit: 'contain' }} />;
  }
  return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--wi-text-secondary)' }}>
      <DeviceGlyph size={40} />
    </div>
  );
}

// Info cell on the white modal — white + the SAME border as the witems form fields
// (--wi-action-primary). A grey fill read as an out-of-place block; a faint --wi-divider
// border was invisible against the white modal.
function AddressSummary({ name, nameClass, address1, address2, zip, city, stateName, country }) {
  return (
    <div style={{ background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-action-primary)', borderRadius: 10, padding: '14px 16px', lineHeight: 1.6, marginTop: 6 }}>
      {name && <div className={`body2${nameClass ? ` ${nameClass}` : ''}`} style={{ fontWeight: 600 }}>{name}</div>}
      <div className="body2">{address1}{address2 ? `, ${address2}` : ''}</div>
      <div className="body2">{[zip, city].filter(Boolean).join(' ')}</div>
      <div className="body2">{[stateName, country].filter(Boolean).join(', ')}</div>
    </div>
  );
}

export default function ShipDeviceModal({ patient, onClose }) {
  const demoMode = useDemoMode();
  const recordQ = usePatientRecord(patient?.userid);
  const orderMut = useCreateDeviceOrder();
  const verifyMut = useVerifyAddress();

  // Catalog (step 1) — single devices AND partner-ref kits, already normalized to one
  // shape by the service. cohortid is a fixed program config (RPM_COHORT_ID), so no
  // getenrolled round-trip is needed to resolve it.
  const cohortid = Constants.COHORT_ID;
  const catalogQ = useCohortProducts(cohortid);
  const catalog = catalogQ.data ?? [];
  const catalogLoading = catalogQ.isLoading;
  const catalogError = catalogQ.isError;

  const [step, setStep] = useState('select');
  const [selectedKey, setSelectedKey] = useState(null);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('US');
  const [stateName, setStateName] = useState('');
  const [zip, setZip] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [verifyErrors, setVerifyErrors] = useState([]);
  const seededRef = useRef(false);
  const blurTimerRef = useRef(null);

  const selectedItem = catalog.find((p) => p.key === selectedKey) ?? null;
  const deviceName = selectedItem?.name ?? '';
  const deviceImg = selectedItem ? productImageUrl(selectedItem) : null;
  const recordName = [recordQ.data?.first_name, recordQ.data?.last_name].filter(Boolean).join(' ');
  const recipientName = recordName || [patient?.firstname, patient?.lastname].filter(Boolean).join(' ');

  // Escape to close + body scroll lock.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  // Clear the pending suggestion-dismiss timer on unmount (avoids setState-after-unmount).
  useEffect(() => () => clearTimeout(blurTimerRef.current), []);

  // One-time pre-fill from the patient's stored address (no country column → US).
  // Only fills still-empty fields so a late-arriving record can't clobber edits.
  useEffect(() => {
    if (seededRef.current) return;
    const r = recordQ.data;
    if (!r) return;
    seededRef.current = true;
    setAddress1((prev) => prev || (r.address_line1 ?? ''));
    setAddress2((prev) => prev || (r.address_line2 ?? ''));
    setCity((prev) => prev || (r.city ?? ''));
    setZip((prev) => prev || (r.zip_code ?? ''));
    setStateName((prev) => prev || toStateName(r.state, 'US'));
  }, [recordQ.data]);

  const debouncedAddress = useDebounce(address1, 250);
  const suggestionsQ = useAddressSuggestions(debouncedAddress, showSuggestions);
  const suggestions = (showSuggestions && suggestionsQ.data) || [];

  function pickSuggestion(s) {
    const parsed = parseAddressSuggestion(s);
    setAddress1(parsed.address1);
    if (parsed.zip) setZip(parsed.zip);
    if (parsed.country && COUNTRIES.some((c) => c.code === parsed.country)) setCountry(parsed.country);
    if (parsed.stateName) setStateName(parsed.stateName);
    setShowSuggestions(false);
  }

  function goToConfirm() {
    if (!address1.trim() || !city.trim() || !stateName.trim() || !zip.trim() || !country) {
      setValidationError('Please fill in the street address, ZIP code, city, state and country.');
      return;
    }
    setValidationError('');
    setStep('confirm');
  }

  function currentOrderAddress() {
    return {
      address1: address1.trim(),
      address2: address2.trim(),
      city: city.trim(),
      country,
      state: stateName,
      zip: zip.trim(),
    };
  }

  function placeOrder() {
    if (!selectedItem) return;
    orderMut.mutate(
      { userid: patient.userid, ...orderRefsFor(selectedItem), orderAddress: currentOrderAddress() },
      { onSuccess: () => setStep('success') },
    );
  }

  // Always verify the address before shipping. Valid → place the order; invalid (or a
  // verifier outage) → show the warning screen so the user can edit or ship anyway.
  async function handleConfirmShip() {
    setVerifyErrors([]);
    try {
      const result = await verifyMut.mutateAsync(currentOrderAddress());
      if (result.valid) placeOrder();
      else { setVerifyErrors(result.errors); setStep('invalid'); }
    } catch {
      setVerifyErrors(['We couldn’t check this address right now.']);
      setStep('invalid');
    }
  }

  const shipping = verifyMut.isPending || orderMut.isPending;

  const subtitle = {
    select: 'Choose a device to ship.',
    address: 'Where should we send it?',
    confirm: 'Review the order before sending it.',
    invalid: '',
    success: '',
  }[step];

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <button className="modal-close" aria-label="Close" onClick={onClose}>×</button>

        <div className="modal-header">
          <h3 style={{ textTransform: 'none' }}>Order a device</h3>
          {subtitle && <p className="body2 text-secondary" style={{ margin: 0 }}>{subtitle}</p>}
        </div>

        {/* Selected device summary (after the device is chosen) */}
        {(step === 'address' || step === 'confirm' || step === 'invalid') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 14px', background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-action-primary)', borderRadius: 10 }}>
            {deviceImg
              ? <img src={deviceImg} alt={deviceName} style={{ width: 48, height: 48, objectFit: 'contain' }} />
              : <span style={{ color: 'var(--wi-text-secondary)' }}><DeviceGlyph size={36} /></span>}
            <div>
              <div className="detail1 text-secondary">Shipping</div>
              <div className="body2" style={{ fontWeight: 600 }}>{deviceName}</div>
            </div>
          </div>
        )}

        {/* ── Step: select device ── */}
        {step === 'select' && (
          <>
            {catalogLoading && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {[0, 1].map((i) => <div key={i} className="skeleton-block" style={{ height: 184, borderRadius: 12 }} />)}
              </div>
            )}
            {!catalogLoading && catalogError && (
              <div style={{ background: 'var(--wi-background-primary)', borderRadius: 12, border: '1px dashed var(--wi-divider)', padding: '28px 24px', textAlign: 'center' }}>
                <p className="body2" style={{ margin: '0 0 4px', fontWeight: 600 }}>Couldn&apos;t load the device catalog</p>
                <p className="detail1 text-secondary" style={{ margin: '0 0 16px' }}>
                  Please try again.
                </p>
                <button type="button" className="btn-secondary" onClick={() => catalogQ.refetch()}>Retry</button>
              </div>
            )}
            {!catalogLoading && !catalogError && catalog.length === 0 && (
              <div style={{ background: 'var(--wi-background-primary)', borderRadius: 12, border: '1px dashed var(--wi-divider)', padding: '28px 24px', textAlign: 'center' }}>
                <p className="body2" style={{ margin: 0, fontWeight: 600 }}>No devices available for this program.</p>
              </div>
            )}
            {!catalogLoading && !catalogError && catalog.length > 0 && (
              <div role="radiogroup" aria-label="Available devices" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {catalog.map((p) => {
                  const selected = selectedKey === p.key;
                  const name = p.name;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setSelectedKey(p.key)}
                      style={{
                        position: 'relative', display: 'flex', flexDirection: 'column', gap: 8, padding: 12, cursor: 'pointer', textAlign: 'left',
                        background: selected ? 'var(--wi-status-info-10)' : 'var(--wi-background-primary)',
                        border: `1px solid ${selected ? 'var(--wi-action-primary)' : 'var(--wi-divider)'}`,
                        boxShadow: selected ? '0 0 0 2px var(--wi-action-primary)' : undefined,
                        borderRadius: 12, fontFamily: 'var(--wi-font-family)',
                      }}
                    >
                      {selected && (
                        <span style={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: '50%', background: 'var(--wi-action-primary)', color: 'var(--wi-on-action-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                        </span>
                      )}
                      <CatalogImage item={p} name={name} />
                      <span className="body2" style={{ fontWeight: 600, color: 'var(--wi-text-primary)' }}>{name}</span>
                      {!p.namedByCatalog && <span className="detail1 text-secondary" style={{ fontFamily: 'monospace' }}>Ref {p.ref}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Step: address ── */}
        {step === 'address' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {recipientName && (
              <p className="detail1 text-secondary" style={{ gridColumn: '1 / -1', margin: 0 }}>
                Shipping to <strong className={demoMode ? 'demo-blur' : undefined} style={{ color: 'var(--wi-text-primary)' }}>{recipientName}</strong>
              </p>
            )}
            <div style={{ position: 'relative' }}>
              <Field
                label="Address (street, number)"
                value={address1}
                placeholder="225 Franklin St"
                onChange={(v) => { setAddress1(String(v)); setShowSuggestions(true); }}
                onBlur={() => { blurTimerRef.current = setTimeout(() => setShowSuggestions(false), 150); }}
              />
              {suggestions.length > 0 && (
                <div
                  style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, background: 'var(--wi-background-secondary)', border: '1px solid var(--wi-action-primary)', borderRadius: 8, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden' }}
                >
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--wi-font-family)', fontSize: 13, color: 'var(--wi-text-primary)' }}
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Field label="Apartment number (optional)" value={address2} placeholder="Apt, suite, floor" onChange={(v) => setAddress2(String(v))} />
            <Field label="ZIP code" value={zip} placeholder="02110" onChange={(v) => setZip(String(v))} />
            <Field label="City" value={city} placeholder="Boston" onChange={(v) => setCity(String(v))} />

            <Select label="Country" value={country} onChange={(v) => { setCountry(String(v)); setStateName(''); }}>
              {COUNTRIES.map((c) => <Option key={c.code} value={c.code}>{c.name}</Option>)}
            </Select>

            <Select label="State" value={stateName} searchable placeholder="Select a state" onChange={(v) => setStateName(String(v))}>
              {statesForCountry(country).map((s) => <Option key={s.code} value={s.name}>{s.name}</Option>)}
            </Select>

            {validationError && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Alert type={StatusType.BAD} text={validationError} />
              </div>
            )}
          </div>
        )}

        {/* ── Step: confirm ── */}
        {step === 'confirm' && (
          <div>
            <span className="eyebrow text-secondary">Shipping address</span>
            <AddressSummary name={recipientName} nameClass={demoMode ? 'demo-blur' : undefined} address1={address1} address2={address2} zip={zip} city={city} stateName={stateName} country={country} />
            {orderMut.isError && (
              <div style={{ marginTop: 16 }}>
                <Alert type={StatusType.BAD} text="We couldn’t place this order. Please try again." />
              </div>
            )}
          </div>
        )}

        {/* ── Step: address could not be verified ── */}
        {step === 'invalid' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: 'var(--wi-status-moderate-10)', borderRadius: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--wi-status-moderate)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4" /><path d="M12 17h.01" />
              </svg>
              <div>
                <p className="body2" style={{ margin: '0 0 2px', fontWeight: 600 }}>We couldn&apos;t verify this address</p>
                <p className="detail1 text-secondary" style={{ margin: 0, lineHeight: 1.5 }}>
                  {(verifyErrors.length ? verifyErrors.join('. ') : 'The address may be incomplete or incorrect.')} You can edit it or ship anyway.
                </p>
              </div>
            </div>
            <span className="eyebrow text-secondary" style={{ display: 'block', marginTop: 16 }}>Shipping address</span>
            <AddressSummary name={recipientName} nameClass={demoMode ? 'demo-blur' : undefined} address1={address1} address2={address2} zip={zip} city={city} stateName={stateName} country={country} />
            {orderMut.isError && (
              <div style={{ marginTop: 16 }}>
                <Alert type={StatusType.BAD} text="We couldn’t place this order. Please try again." />
              </div>
            )}
          </div>
        )}

        {/* ── Step: success ── */}
        {step === 'success' && (
          <div role="status" style={{ textAlign: 'center', padding: '24px 0 12px' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--wi-status-good-10)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--wi-status-good)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <p className="header4" style={{ margin: '0 0 4px' }}>Order placed</p>
            <p className="body2 text-secondary" style={{ margin: '0 0 20px' }}>
              {deviceName} is on its way to the patient.
            </p>
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </div>
        )}

        {/* Footer */}
        {step !== 'success' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            {step === 'select' && (
              <>
                <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                <button type="button" className="btn-primary" onClick={() => setStep('address')} disabled={!selectedKey}>Next</button>
              </>
            )}
            {step === 'address' && (
              <>
                <button type="button" className="btn-secondary" onClick={() => setStep('select')}>Back</button>
                <button type="button" className="btn-primary" onClick={goToConfirm}>Next</button>
              </>
            )}
            {step === 'confirm' && (
              <>
                <button type="button" className="btn-secondary" onClick={() => setStep('address')} disabled={shipping}>Back</button>
                <button type="button" className="btn-primary" onClick={handleConfirmShip} disabled={shipping}>
                  {verifyMut.isPending ? 'Verifying address…' : orderMut.isPending ? 'Placing order…' : 'Confirm & ship'}
                </button>
              </>
            )}
            {step === 'invalid' && (
              <>
                <button type="button" className="btn-secondary" onClick={placeOrder} disabled={orderMut.isPending}>{orderMut.isPending ? 'Placing order…' : 'Ship anyway'}</button>
                <button type="button" className="btn-primary" onClick={() => setStep('address')} disabled={orderMut.isPending}>Edit address</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  ), document.body);
}
